import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Phone } from "@phosphor-icons/react";

const LINES = [
    { text: "CONTAINERS.", style: "text-white" },
    { text: "MACHINERY.", style: "text-outline" },
    { text: "FREIGHT.", style: "text-blaze" },
];

const lineVariants = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.35 + i * 0.16, duration: 1.05, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 60, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 18 });

    const handleMouseMove = (e) => {
        const rect = sectionRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    return (
        <section
            id="top"
            ref={sectionRef}
            onMouseMove={handleMouseMove}
            className="grid-backdrop relative flex min-h-screen flex-col justify-end overflow-hidden px-5 pb-10 pt-28 md:px-10 md:pb-14"
            data-testid="hero-section"
        >
            <div className="pointer-events-none absolute left-1/2 top-0 h-[60vh] w-[80vw] -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgba(255,61,0,0.10),transparent_60%)]" />

            <div className="grid flex-1 items-center gap-10 lg:grid-cols-12">
                <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-8">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25, duration: 0.7 }}
                        className="mb-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50 md:text-xs"
                        data-testid="hero-eyebrow"
                    >
                        <span className="inline-block h-2 w-2 bg-blaze" />
                        Akash Group of Companies — Transport · Haulage · Freight
                    </motion.p>

                    <h1 className="font-display uppercase leading-[0.92] tracking-tight">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    custom={i}
                                    variants={lineVariants}
                                    initial="hidden"
                                    animate="show"
                                    className={`block text-[15vw] md:text-[10.5vw] lg:text-[9vw] ${line.style}`}
                                    data-testid={`hero-line-${i + 1}`}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.8 }}
                        className="mt-8 max-w-md text-base leading-relaxed text-white/60 md:text-lg"
                        data-testid="hero-subcopy"
                    >
                        Three companies. One promise. 20 ft &amp; 40 ft container transport,
                        heavy machinery haulage and end-to-end freight forwarding — across India.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.25, duration: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#enquiry"
                            data-testid="hero-quote-cta"
                            className="group flex items-center gap-3 bg-blaze px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-white hover:text-void"
                        >
                            Get a Quote
                            <ArrowDown size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-y-0.5" />
                        </a>
                        <a
                            href="tel:+919711220094"
                            data-testid="hero-call-cta"
                            className="group flex items-center gap-3 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:border-blaze hover:text-blaze"
                        >
                            <Phone size={15} weight="bold" />
                            +91 97112 20094
                        </a>
                    </motion.div>
                </motion.div>

                <div className="relative hidden lg:col-span-4 lg:block" style={{ perspective: 1000 }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.9, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        style={{ rotateX, rotateY }}
                        className="relative"
                    >
                        <div className="overflow-hidden border border-white/20" data-testid="hero-image-frame">
                            <motion.img
                                src="https://images.pexels.com/photos/14020705/pexels-photo-14020705.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                alt="Cargo port with stacked shipping containers at sunrise"
                                style={{ y: imageY, scale: 1.18 }}
                                className="aspect-[4/5] w-full object-cover"
                                data-testid="hero-image"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
                        </div>
                        <div className="absolute -bottom-5 -left-5 border border-blaze bg-void px-4 py-3" data-testid="hero-image-badge">
                            <p className="font-display text-2xl uppercase text-blaze">PAN INDIA</p>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">Port to door network</p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="mt-16 flex items-end justify-between border-t border-white/10 pt-5"
            >
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 md:text-xs" data-testid="hero-footer-left">
                    Est. 2007 — New Delhi, India
                </p>
                <div className="flex items-center gap-3" data-testid="hero-scroll-indicator">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 md:text-xs">Scroll</span>
                    <span className="block h-10 w-px overflow-hidden bg-white/10">
                        <span className="block h-full w-full animate-scroll-pulse bg-blaze" />
                    </span>
                </div>
            </motion.div>
        </section>
    );
}
